import _ from 'underscore';

/**
 * This exposed function returns a wrapper function that creates a promise instead of counting on a callback
 * @param {Function} fun - the function to wrap
 * @param {{success: Function | string, error: Function | string} | boolean | string } [callbackResolution] - defines where to find the callback
 * @returns {Function} a wrapper function that will return a promise
 */
function makeAsync(fun, callbackResolution) {
    let wrapper;
    switch (typeof callbackResolution) {
        case "boolean":
            wrapper = callbackResolution ? unshiftPromiseWrapper : pushPromiseWrapper;
            break;
        case "object":
            wrapper = decideWrapper(callbackResolution);
            break;
        case "string":
            wrapper = wrapConfigObjectCb.bind(this, callbackResolution, null);
            break;
        case "undefined":
        default:
            wrapper = pushPromiseWrapper;
    }
    return function () {
        let resolve = _.noop(), reject = _.noop();
        const p = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        const wrappedArgs = wrapper(arguments, resolve, reject);
        let result = fun(...wrappedArgs);
        if (typeof result !== "object")
            result = {retVal: result};
        return _.extend(p, result);
    };
}

function pushPromiseWrapper(args, resolve) {
    return pushBothPromiseWrapper(false, args, resolve, _.noop);
}

/**
 * This function wraps the callback provided to another function with a promise resolution callback
 * this practically means that after the wrapped asynchronous function calls its callback
 * (usually ending its actions right after that), the promise returned by the wrapper will be resolved.
 * @param {boolean} both - whether to add both resolution and rejection callbacks
 * @param {arguments} args - the arguments object of the wrapped function
 * @param {Function} resolve - the resolve() function of a new promise
 * @param {Function} reject - the reject() function of a new promise
 * @returns {Array} the tweaked arguments array including the resolver function where the callback was
 */
function pushBothPromiseWrapper(both, args, resolve, reject) {
    if (!_.isArguments(args))
        throw new Error("No arguments object provided");
    let initialArgs = _.initial(args);
    let callback = _.last(args);
    let rejectionCb = _.noop;

    if (both) {
        rejectionCb = callback;
        callback = _.last(initialArgs);
        initialArgs = _.initial(initialArgs);
        if (!_.isFunction(callback)) {
            if (!_.isUndefined(callback))
                initialArgs.push(callback);
            callback = _.noop;
        }
        if (!_.isFunction(rejectionCb)) {
            if (!_.isUndefined(rejectionCb))
                initialArgs.push(rejectionCb);
            rejectionCb = _.noop;
        }
    } else if (!_.isFunction(callback)) {
        if (!_.isUndefined(callback))
            initialArgs.push(callback);
        callback = _.noop;
    }

    const wrapped = attachPromiseToCb(callback, resolve, rejectionCb, reject);

    let wrappedArgs = initialArgs.concat(wrapped.resolve);
    if (both)
        wrappedArgs = wrappedArgs.concat(wrapped.reject);
    return wrappedArgs;
}

/**
 * Same as {pushPromiseWrapper}, with the difference of placing the callback in the first position of the
 * arguments array.
 * @param {arguments} args
 * @param {Function} resolve
 * @param {Function} reject
 * @returns {Array}
 */
function unshiftPromiseWrapper(args, resolve, reject) {
    if (!_.isArguments(args))
        throw new Error("No arguments object provided");
    let [first, ...rest] = args;

    if (!_.isFunction(first)) {
        if (!_.isUndefined(first))
            rest.unshift(first);
        first = _.noop;
    }

    const wrapped = attachPromiseToCb(first, resolve, _.noop, reject);

    return [wrapped.resolve].concat(rest);
}

/**
 * This function wraps the callbacks contained in a configuration object
 * @param {string} successCb - the name of the success callback
 * @param {string} errorCb - the name of the error callback
 * @param {arguments} args - the args object. the config object is supposed to be the last item
 * @param {Function} resolve
 * @param {Function} reject
 * @returns {Array}
 */
function wrapConfigObjectCb(successCb, errorCb, args, resolve, reject) {

    if (!_.isArguments(args))
        throw new Error("No arguments object provided");
    let initial = _.initial(args);
    let last = _.last(args);
    //fallback for multiple implementations.
    if (!_.isObject(last))
        return pushPromiseWrapper(args, resolve, reject);

    let successHandler = last[successCb];
    if (!_.isFunction(successHandler)) {
        successHandler = _.noop;
    }
    let errorHandler = last[errorCb];
    if (!_.isFunction(errorHandler)) {
        errorHandler = _.noop;
    }

    const wrapped = attachPromiseToCb(successHandler, resolve, errorHandler, reject);

    last[successCb] = wrapped.resolve;
    last[errorCb] = wrapped.reject;
    return initial.concat(last);
}

/**
 * This helper returns two functions that run the successCallback and resolve/reject the promise respectively
 * @param successCallback
 * @param resolve
 * @param errorCallback
 * @param reject
 * @returns {{resolve: Function, reject: Function}}
 */
function attachPromiseToCb(successCallback, resolve, errorCallback, reject) {

    function resolvingCb() {
        successCallback(...arguments);
        resolve(...arguments);
    }

    function rejectingCb() {
        errorCallback(...arguments);
        reject(...arguments);
    }

    return {resolve: resolvingCb, reject: rejectingCb};
}

function decideWrapper(callbackResolution) {
    const res = callbackResolution.success;
    const rej = callbackResolution.error;
    switch (callbackResolution.mode) {
        case "push":
            return pushBothPromiseWrapper.bind(this, true);
        case "unshift":
            break;
        case "config":
        default:
            return wrapConfigObjectCb.bind(this, res, rej);
    }
}

export default makeAsync;
